import type { IProfileResponse, IUpdateProfileDTO } from '../api/types'
import * as Sentry from '@sentry/vue'
import { useQueryClient } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { clearAuthTokens } from '@/shared/lib'
import { useFetchProfileQuery, useUpdateProfileMutation } from '../api/authApi'

export const useAuthStore = defineStore('auth', () => {
  // State
  const profile = ref<IProfileResponse | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // TanStack Query client so `logout()` can wipe ALL cached queries, not just
  // the ones a particular caller remembers. Otherwise a forced logout would
  // leave profile/stores/etc. cached, and the next screen could read them
  // back and render a ghost "logged in" state.
  const queryClient = useQueryClient()

  // Profile query
  const {
    data: profileData,
    isLoading: isProfileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useFetchProfileQuery()

  // Sync profile with profileData from query
  watch(profileData, (newData) => {
    if (newData) {
      profile.value = newData
      // Tag Sentry events with the active user so we can find a single
      // user's session quickly (e.g. when investigating a forced_logout).
      Sentry.setUser({
        id: String(newData.id),
        email: newData.email,
      })
    }
  })

  // Update profile mutation
  const {
    mutate: updateProfile,
    isPending: isUpdatingProfile,
    error: updateProfileError,
  } = useUpdateProfileMutation()

  // Computed
  const isAuthenticated = computed(() => {
    // Consider user authenticated if profile is available (reactive)
    return Boolean(profile.value)
  })

  const userFullName = computed(() => {
    if (!profileData.value) 
      return ''

    const { first_name, last_name, full_name } = profileData.value
    if (full_name) 
      return full_name
    if (first_name && last_name) 
      return `${first_name} ${last_name}`
    if (first_name) 
      return first_name
    if (last_name) 
      return last_name
    return profileData.value.email
  })

  const userEmail = computed(() => {
    return profileData.value?.email || ''
  })

  // Actions
  async function fetchProfile() {
    try {
      isLoading.value = true
      const result = await refetchProfile()
      if (result.data) {
        profile.value = result.data
      }
    }
    catch (err) {
      error.value = 'Failed to fetch profile'
      console.error(err)
    }
    finally {
      isLoading.value = false
    }
  }

  async function updateUserProfile(profileData: IUpdateProfileDTO) {
    return new Promise((resolve, reject) => {
      updateProfile(profileData, {
        onSuccess: (data) => {
          // Update local profile state
          profile.value = data
          // Refetch profile to ensure consistency
          refetchProfile()
          resolve(data)
        },
        onError: (error) => {
          reject(error)
        },
      })
    })
  }

  function clearProfile() {
    profile.value = null
    error.value = null
    // Detach Sentry user — subsequent events should not be attributed to
    // the previous account.
    Sentry.setUser(null)
  }

  function logout() {
    clearProfile()
    clearAuthTokens()
    // Wipe every cached query. If we only cleared the auth store, a cached
    // `['profile']` response would flow back through useFetchProfileQuery's
    // data watcher on the next mount and flip `isAuthenticated` back to true,
    // causing a brief "logged in" flash on the landing page after a forced
    // logout. removeQueries() with no filter clears the whole cache.
    queryClient.removeQueries()
  }

  return {
    // State
    profile,
    isLoading: computed(() => isProfileLoading.value || isLoading.value),
    error: computed(() => profileError.value?.message || error.value),

    // Computed
    isAuthenticated,
    userFullName,
    userEmail,

    // Actions
    fetchProfile,
    updateUserProfile,
    clearProfile,
    logout,

    // Update profile state
    isUpdatingProfile: computed(() => isUpdatingProfile.value),
    updateProfileError: computed(() => updateProfileError.value?.message),
  }
})
